import {Action} from '@ngrx/store';
import {Shift} from '@app/core/models/shift.model';
import {
	PERIOD_TYPE,
	VIEW_TYPE
} from '@app/modules/home/modules/schedule/components/planning-controls/planning-controls.component';
import {Moment} from 'moment';
import {Group} from '@app/core/models/group.model';
import {User} from '@app/core/models/user.model';
import {IPatchShiftParams, IPostShiftParams} from '@app/modules/shared/services/shift.service';
import {Availability} from '@app/core/models/availability.model';
import {SORTING_TYPE} from '@app/modules/home/modules/schedule/components/planning-header/planning-header.component';

export enum EScheduleActions {
	GetShift = '[Schedule] Get Shift',
	GetShiftSuccess = '[Schedule] Get Shift Success',
	GetShifts = '[Schedule] Get Schedule',
	GetShiftsSuccess = '[Schedule] Get Schedule Success',
	GetUserShifts = '[Schedule] Get User Shifts',
	GetUserShiftsSuccess = '[Schedule] Get User Shifts Success',
	SetPeriodType = '[Schedule] Set period type',
	SetViewType = '[Schedule] Set view type',
	SetSelectedDates = '[Schedule] Set selected dates',
	SetSelectedStartWeek = '[Schedule] Set selected start week',
	GetUsers = '[Schedule] Get Users',
	GetUsersSuccess = '[Schedule] Get Users Success',
	GetGroups = '[Schedule] Get Groups',
	GetGroupsSuccess = '[Schedule] Get Groups Success',
	SetSelectedShift = '[Schedule] Set Selected Shift',
	PatchShift = '[Schedule] Patch Shift',
	PatchShiftSuccess = '[Schedule] Patch Shift Success',
	PostShift = '[Schedule] Post Shift',
	PostShiftSuccess = '[Schedule] Post Shift Success',
	DeleteShift = '[Schedule] Delete Shift',
	DeleteShiftSuccess = '[Schedule] Delete Shift Success',
	SetSelectedDate = '[Schedule] Set Selected Date',
	GetAvailabilities = '[Schedule] Get Availabilities',
	GetAvailabilitiesSuccess = '[Schedule] Get Availabilities Success',
	SetIsCopying = '[Schedule] Set Is Copying',
	SetDraggingShift = '[Schedule] Set Dragging shift',
	SetSelectedModel = '[Schedule] Set Selected Model',
	SetSortingType = '[Schedule] Set Sorting Mode',
	AddShiftToStock = '[Schedule] Add Shift To Stock',
	AddShiftToStockSuccess = '[Schedule] Add Shift To Stock Success',
	RemoveShiftFromStock = '[Schedule] Remove Shift From Stock',
	RemoveShiftFromStockSuccess = '[Schedule] Remove Shift From Stock Success',
	AddShiftStockBid = '[Schedule] Add Shift Stock Bid',
	AddShiftStockBidSuccess = '[Schedule] Add Shift Stock Bid Success',
	RemoveShiftStockBid = '[Schedule] Remove Shift Stock Bid',
	RemoveShiftStockBidSuccess = '[Schedule] Remove Shift Stock Bid Success',
	DeleteShiftsInInterval = '[Schedule] Delete Shifts In Interval',
	DeleteShiftsInIntervalSuccess = '[Schedule] Delete Shifts In Interval Success',
	SetUsersWhoBid = '[Schedule] Set Users Who Bid',
	SetAvailableUsers = '[Schedule] Set Available Users',
	SetFetchedShift = '[Schedule] Set Fetched Shift',
	SetScrollLeft = '[Schedule] Set Scroll Left',
	SetPrinting = '[Schedule] Set Printing',

	SetReceivedDepartmentIdsCachingCalculation = '[Schedule] Set Received Department Ids Caching Calculation',
	SetHasShiftChange = '[Schedule] Set Has Shift Change',
}

export class GetShifts implements Action {
	public readonly type = EScheduleActions.GetShifts;

	constructor(public startDate: Moment, public endDate: Moment) {
	}
}

export class GetShiftsSuccess implements Action {
	public readonly type = EScheduleActions.GetShiftsSuccess;

	constructor(public shifts: Shift[]) {
	}
}

export class GetUserShifts implements Action {
	public readonly type = EScheduleActions.GetUserShifts;

	constructor(public userId: number) {
	}
}

export class GetUserShiftsSuccess implements Action {
	public readonly type = EScheduleActions.GetUserShiftsSuccess;

	constructor(public shifts: Shift[]) {
	}
}

export class GetAvailabilities implements Action {
	public readonly type = EScheduleActions.GetAvailabilities;

	constructor(public startDate: Moment, public endDate: Moment) {
	}
}

export class GetAvailabilitiesSuccess implements Action {
	public readonly type = EScheduleActions.GetAvailabilitiesSuccess;

	constructor(public availabilities: Availability[]) {
	}
}

export class SetPeriodType implements Action {
	public readonly type = EScheduleActions.SetPeriodType;

	constructor(public periodType: PERIOD_TYPE) {
	}
}

export class SetIsCopying implements Action {
	public readonly type = EScheduleActions.SetIsCopying;

	constructor(public isCopying: boolean) {
	}
}

export class SetDraggingShift implements Action {
	public readonly type = EScheduleActions.SetDraggingShift;

	constructor(public shift: Shift | undefined) {
	}
}

export class SetSelectedModel implements Action {
	public readonly type = EScheduleActions.SetSelectedModel;

	constructor(public model: Group | User) {
	}
}

export class SetViewType implements Action {
	public readonly type = EScheduleActions.SetViewType;

	constructor(public viewType: VIEW_TYPE) {
	}
}

export class SetSelectedDates implements Action {
	public readonly type = EScheduleActions.SetSelectedDates;

	constructor(public selectedDates: Moment[]) {
	}
}

export class SetSelectedDate implements Action {
	public readonly type = EScheduleActions.SetSelectedDate;

	constructor(public selectedDate: Moment) {
	}
}

export class SetSelectedStartWeek implements Action {
	public readonly type = EScheduleActions.SetSelectedStartWeek;
	public weekNumber: number;
	public year: number;

	constructor(public date: Moment) {
		this.weekNumber = date.isoWeek();
		this.year = date.isoWeekYear();
	}
}

export class GetUsers implements Action {
	public readonly type = EScheduleActions.GetUsers;

	constructor(public departmentId: number) {
	}
}

export class GetUsersSuccess implements Action {
	public readonly type = EScheduleActions.GetUsersSuccess;

	constructor(public users: User[]) {
	}
}

export class GetGroups implements Action {
	public readonly type = EScheduleActions.GetGroups;

	constructor(public departmentId: number) {
	}
}

export class GetGroupsSuccess implements Action {
	public readonly type = EScheduleActions.GetGroupsSuccess;

	constructor(public groups: Group[]) {
	}
}

export class SetSelectedShift implements Action {
	public readonly type = EScheduleActions.SetSelectedShift;

	constructor(public shift: Shift | null | undefined, public selectedDate: Moment) {
	}
}

export class SetSortingType implements Action {
	public readonly type = EScheduleActions.SetSortingType;

	constructor(public sortingType: SORTING_TYPE) {
	}
}

export class SetUsersWhoBid implements Action {
	public readonly type = EScheduleActions.SetUsersWhoBid;

	constructor(public usersWhoBid: User[]) {
	}
}

export class SetAvailableUsers implements Action {
	public readonly type = EScheduleActions.SetAvailableUsers;

	constructor(public availableUsers: User[]) {
	}
}

export class SetFetchedShift implements Action {
	public readonly type = EScheduleActions.SetFetchedShift;

	constructor(public fetchedShift: Shift | null) {
	}
}

export class PatchShift implements Action {
	public readonly type = EScheduleActions.PatchShift;

	constructor(public id: number, public payload: IPatchShiftParams) {
	}
}

export class PatchShiftSuccess implements Action {
	public readonly type = EScheduleActions.PatchShiftSuccess;

	constructor(public shift: Shift, public originalShiftId: number) {
	}
}

export class PostShift implements Action {
	public readonly type = EScheduleActions.PostShift;

	constructor(public payload: IPostShiftParams) {
	}
}

export class PostShiftSuccess implements Action {
	public readonly type = EScheduleActions.PostShiftSuccess;

	constructor(public shift: Shift) {
	}
}

export class DeleteShift implements Action {
	public readonly type = EScheduleActions.DeleteShift;

	constructor(public shift: Shift) {
	}
}

export class DeleteShiftSuccess implements Action {
	public readonly type = EScheduleActions.DeleteShiftSuccess;

	constructor(public shift: Shift) {
	}
}

export class AddShiftToStock implements Action {
	public readonly type = EScheduleActions.AddShiftToStock;

	constructor(public shiftId: number) {
	}
}

export class AddShiftToStockSuccess implements Action {
	public readonly type = EScheduleActions.AddShiftToStockSuccess;

	constructor(public shiftId: number, public publicShiftStockId: number) {
	}
}

export class RemoveShiftFromStock implements Action {
	public readonly type = EScheduleActions.RemoveShiftFromStock;

	constructor(public shiftId: number) {
	}
}

export class RemoveShiftFromStockSuccess implements Action {
	public readonly type = EScheduleActions.RemoveShiftFromStockSuccess;

	constructor(public shiftId: number) {
	}
}

export class AddShiftStockBid implements Action {
	public readonly type = EScheduleActions.AddShiftStockBid;

	constructor(public shiftId: number) {
	}
}

export class AddShiftStockBidSuccess implements Action {
	public readonly type = EScheduleActions.AddShiftStockBidSuccess;

	constructor(public shiftId: number) {
	}
}

export class RemoveShiftStockBid implements Action {
	public readonly type = EScheduleActions.RemoveShiftStockBid;

	constructor(public shiftId: number) {
	}
}

export class RemoveShiftStockBidSuccess implements Action {
	public readonly type = EScheduleActions.RemoveShiftStockBidSuccess;

	constructor(public shiftId: number) {
	}
}

export class DeleteShiftsInInterval implements Action {
	public readonly type = EScheduleActions.DeleteShiftsInInterval;

	constructor(public start: Moment, public end: Moment) {
	}
}

export class DeleteShiftsInIntervalSuccess implements Action {
	public readonly type = EScheduleActions.DeleteShiftsInIntervalSuccess;

	constructor() {
	}
}

export class GetShift implements Action {
	public readonly type = EScheduleActions.GetShift;

	constructor(public id: number) {
	}
}

export class GetShiftSuccess implements Action {
	public readonly type = EScheduleActions.GetShiftSuccess;

	constructor(
		public shift: Shift,
		public availableUsers: User[],
		public usersWhoBid: User[],
		public approvedAvailabilitiesUserId: number[],
		public pendingAvailabilitiesUserId: number[],
	) {
	}
}

export class SetScrollLeft implements Action {
	public readonly type = EScheduleActions.SetScrollLeft;

	constructor(public scrollLeft: number) {
	}
}

export class SetPrinting implements Action {
	public readonly type = EScheduleActions.SetPrinting;

	constructor(public printing: boolean) {
	}
}

export class SetReceivedDepartmentIdsCachingCalculation implements Action {
	public readonly type = EScheduleActions.SetReceivedDepartmentIdsCachingCalculation;

	constructor(public shiftIds: { id: number }[]) {
	}
}

export class SetHasShiftChange implements Action {
	public readonly type = EScheduleActions.SetHasShiftChange;

	constructor(public hasChange: boolean) {
	}
}

export type ScheduleActions =
	GetShifts |
	GetShiftsSuccess |
	GetUserShifts |
	GetUserShiftsSuccess |
	SetPeriodType |
	SetViewType |
	SetSelectedDates |
	SetSelectedStartWeek |
	GetUsers |
	GetUsersSuccess |
	GetGroups |
	GetGroupsSuccess |
	SetSelectedShift |
	PatchShift |
	PatchShiftSuccess |
	PostShift |
	PostShiftSuccess |
	DeleteShift |
	DeleteShiftSuccess |
	SetSelectedDate |
	GetAvailabilities |
	GetAvailabilitiesSuccess |
	SetIsCopying |
	SetDraggingShift |
	SetSelectedModel |
	SetSortingType |
	AddShiftToStock |
	AddShiftToStockSuccess |
	RemoveShiftFromStock |
	RemoveShiftFromStockSuccess |
	AddShiftStockBid |
	AddShiftStockBidSuccess |
	RemoveShiftStockBid |
	RemoveShiftStockBidSuccess |
	DeleteShiftsInInterval |
	DeleteShiftsInIntervalSuccess |
	GetShift |
	GetShiftSuccess |
	SetUsersWhoBid |
	SetAvailableUsers |
	SetFetchedShift |
	SetScrollLeft |
	SetPrinting |
	SetReceivedDepartmentIdsCachingCalculation |
	SetHasShiftChange
	;
